import { RiskImpact, TenantVendorRisk } from "@oneleet/api_client";
import { cn } from "@oneleet/utils";

import { cva } from "class-variance-authority";

const fillColorVariants = cva("fill-white", {
  variants: {
    impact: {
      [RiskImpact.MINOR]: "fill-ng-blue-800",
      [RiskImpact.MODERATE]: "fill-ng-yellow-800",
      [RiskImpact.MAJOR]: "fill-ng-red-800",
    },
    vendorRisk: {
      [TenantVendorRisk.LOW]: "fill-ng-blue-800",
      [TenantVendorRisk.MEDIUM]: "fill-ng-yellow-800",
      [TenantVendorRisk.HIGH]: "fill-ng-red-800",
      UNSPECIFIED: "fill-white",
    },
    preview: {
      true: "fill-neutral-200",
    },
    filled: {
      true: "",
      false: "fill-white",
    },
  },
});

const strokeColorVariants = cva("fill-neutral-400", {
  variants: {
    impact: {
      [RiskImpact.MINOR]: "fill-ng-blue-900",
      [RiskImpact.MODERATE]: "fill-ng-yellow-900",
      [RiskImpact.MAJOR]: "fill-ng-red-900",
    },
    vendorRisk: {
      [TenantVendorRisk.LOW]: "fill-ng-blue-900",
      [TenantVendorRisk.MEDIUM]: "fill-ng-yellow-900",
      [TenantVendorRisk.HIGH]: "fill-ng-red-900",
      UNSPECIFIED: "fill-neutral-400",
    },
    preview: {
      true: "fill-neutral-400",
    },
    filled: {
      true: "",
      false: "fill-neutral-400",
    },
  },
});

export function ImpactIndicator({
  impact,
  vendorRisk,
  preview = false,
  className,
}: {
  impact?: RiskImpact;
  vendorRisk?: TenantVendorRisk | "UNSPECIFIED";
  preview?: boolean;
  className?: string;
}) {
  const firstBarIsFilled = shouldFillBar({
    barPosition: 1,
    impact,
    vendorRisk,
  });
  const secondBarIsFilled = shouldFillBar({
    barPosition: 2,
    impact,
    vendorRisk,
  });
  const thirdBarIsFilled = shouldFillBar({
    barPosition: 3,
    impact,
    vendorRisk,
  });

  return (
    <svg
      viewBox="0 0 16 16"
      xmlns="http://www.w3.org/2000/svg"
      className={cn("size-4 shrink-0", className)}
    >
      <mask id="path-1-inside-1_4186_13046" fill="white">
        <path d="M1 4C1 2.89543 1.89543 2 3 2C4.10457 2 5 2.89543 5 4V12C5 13.1046 4.10457 14 3 14C1.89543 14 1 13.1046 1 12V4Z" />
      </mask>
      <path
        d="M1 4C1 2.89543 1.89543 2 3 2C4.10457 2 5 2.89543 5 4V12C5 13.1046 4.10457 14 3 14C1.89543 14 1 13.1046 1 12V4Z"
        fill="currentColor"
        className={cn(
          fillColorVariants({
            impact,
            vendorRisk,
            preview,
            filled: firstBarIsFilled,
          }),
        )}
      />
      <path
        d="M4 4V12H6V4H4ZM2 12V4H0V12H2ZM3 13C2.44772 13 2 12.5523 2 12H0C0 13.6569 1.34315 15 3 15V13ZM4 12C4 12.5523 3.55228 13 3 13V15C4.65685 15 6 13.6569 6 12H4ZM3 3C3.55228 3 4 3.44772 4 4H6C6 2.34315 4.65685 1 3 1V3ZM3 1C1.34315 1 0 2.34315 0 4H2C2 3.44772 2.44772 3 3 3V1Z"
        fill="currentColor"
        mask="url(#path-1-inside-1_4186_13046)"
        className={cn(
          strokeColorVariants({
            impact,
            vendorRisk,
            preview,
            filled: firstBarIsFilled,
          }),
        )}
      />
      <mask id="path-3-inside-2_4186_13046" fill="white">
        <path d="M6 4C6 2.89543 6.89543 2 8 2C9.10457 2 10 2.89543 10 4V12C10 13.1046 9.10457 14 8 14C6.89543 14 6 13.1046 6 12V4Z" />
      </mask>
      <path
        d="M6 4C6 2.89543 6.89543 2 8 2C9.10457 2 10 2.89543 10 4V12C10 13.1046 9.10457 14 8 14C6.89543 14 6 13.1046 6 12V4Z"
        fill="currentColor"
        className={cn(
          fillColorVariants({
            impact,
            vendorRisk,
            preview,
            filled: secondBarIsFilled,
          }),
        )}
      />
      <path
        d="M9 4V12H11V4H9ZM7 12V4H5V12H7ZM8 13C7.44772 13 7 12.5523 7 12H5C5 13.6569 6.34315 15 8 15V13ZM9 12C9 12.5523 8.55228 13 8 13V15C9.65685 15 11 13.6569 11 12H9ZM8 3C8.55228 3 9 3.44772 9 4H11C11 2.34315 9.65685 1 8 1V3ZM8 1C6.34315 1 5 2.34315 5 4H7C7 3.44772 7.44772 3 8 3V1Z"
        fill="currentColor"
        mask="url(#path-3-inside-2_4186_13046)"
        className={cn(
          strokeColorVariants({
            impact,
            vendorRisk,
            preview,
            filled: secondBarIsFilled,
          }),
        )}
      />
      <mask id="path-5-inside-3_4186_13046" fill="white">
        <path d="M11 4C11 2.89543 11.8954 2 13 2C14.1046 2 15 2.89543 15 4V12C15 13.1046 14.1046 14 13 14C11.8954 14 11 13.1046 11 12V4Z" />
      </mask>
      <path
        d="M11 4C11 2.89543 11.8954 2 13 2C14.1046 2 15 2.89543 15 4V12C15 13.1046 14.1046 14 13 14C11.8954 14 11 13.1046 11 12V4Z"
        fill="currentColor"
        className={cn(
          fillColorVariants({
            impact,
            vendorRisk,
            preview,
            filled: thirdBarIsFilled,
          }),
        )}
      />
      <path
        d="M14 4V12H16V4H14ZM12 12V4H10V12H12ZM13 13C12.4477 13 12 12.5523 12 12H10C10 13.6569 11.3431 15 13 15V13ZM14 12C14 12.5523 13.5523 13 13 13V15C14.6569 15 16 13.6569 16 12H14ZM13 3C13.5523 3 14 3.44772 14 4H16C16 2.34315 14.6569 1 13 1V3ZM13 1C11.3431 1 10 2.34315 10 4H12C12 3.44772 12.4477 3 13 3V1Z"
        fill="currentColor"
        mask="url(#path-5-inside-3_4186_13046)"
        className={cn(
          strokeColorVariants({
            impact,
            vendorRisk,
            preview,
            filled: thirdBarIsFilled,
          }),
        )}
      />
    </svg>
  );
}

const shouldFillBar = ({
  barPosition,
  impact,
  vendorRisk,
}: {
  barPosition: 1 | 2 | 3;
  impact?: RiskImpact;
  vendorRisk?: TenantVendorRisk | "UNSPECIFIED";
}): boolean => {
  if (!impact && !vendorRisk) return false;

  switch (barPosition) {
    case 1:
      return true;
    case 2:
      return (
        impact === RiskImpact.MODERATE ||
        impact === RiskImpact.MAJOR ||
        vendorRisk === TenantVendorRisk.MEDIUM ||
        vendorRisk === TenantVendorRisk.HIGH
      );
    case 3:
      return (
        impact === RiskImpact.MAJOR || vendorRisk === TenantVendorRisk.HIGH
      );
    default:
      return false;
  }
};
